import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { adminDiagnosticsApi, type DiagnosticAssignmentRow } from '@/api/adminDiagnostics'
import { ModalShell } from './ModalShell'

interface Props {
  assignment: DiagnosticAssignmentRow
  studentName: string
  onClose: () => void
  onDone: () => void
}

export function ResetModal({ assignment, studentName, onClose, onDone }: Props) {
  const { t } = useTranslation('admin')
  const status = assignment.status
  const levelGraded = Boolean(assignment.levelGraded)
  const canResetAiq =
    status === 'AIQ_IN_PROGRESS' || status === 'LEVEL_GRADING' || status === 'COMPLETED'
  const canResetLevel = status === 'COMPLETED' || levelGraded

  const resetAiq = useMutation({
    mutationFn: () => adminDiagnosticsApi.resetAiq(assignment.id),
    onSuccess: onDone,
  })
  const resetLevel = useMutation({
    mutationFn: () => adminDiagnosticsApi.resetLevel(assignment.id),
    onSuccess: onDone,
  })
  const pending = resetAiq.isPending || resetLevel.isPending
  const failed = resetAiq.isError || resetLevel.isError

  return (
    <ModalShell title={t('diagnostic.reset.title', { name: studentName })} onClose={onClose}>
      <div className="space-y-4">
        {!canResetAiq && !canResetLevel && (
          <p className="text-sm text-gray-500">{t('diagnostic.reset.none')}</p>
        )}

        {canResetAiq && (
          <section className="border rounded-lg p-3">
            <h3 className="text-sm font-semibold text-gray-900">{t('diagnostic.reset.aiqOption')}</h3>
            <p className="mt-1 text-sm text-gray-600">{t('diagnostic.reset.aiqDesc')}</p>
            {status === 'AIQ_IN_PROGRESS' && (
              <p className="mt-1 text-sm text-amber-600">{t('diagnostic.reset.aiqInProgressWarning')}</p>
            )}
            <button
              type="button"
              disabled={pending}
              onClick={() => resetAiq.mutate()}
              className="mt-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
            >
              {t('diagnostic.reset.confirm')}
            </button>
          </section>
        )}

        {canResetLevel && (
          <section className="border rounded-lg p-3">
            <h3 className="text-sm font-semibold text-gray-900">{t('diagnostic.reset.levelOption')}</h3>
            <p className="mt-1 text-sm text-gray-600">{t('diagnostic.reset.levelDesc')}</p>
            <button
              type="button"
              disabled={pending}
              onClick={() => resetLevel.mutate()}
              className="mt-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm disabled:opacity-50"
            >
              {t('diagnostic.reset.confirm')}
            </button>
          </section>
        )}

        {failed && <p className="text-sm text-red-600">{t('diagnostic.reset.failed')}</p>}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm"
          >
            {t('diagnostic.reset.cancel')}
          </button>
        </div>
      </div>
    </ModalShell>
  )
}
